import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { authApi } from '@/lib/erpApi';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Building2 } from 'lucide-react';

const blank = {
  name: '', address: '', city: 'Bhilai', state: 'Chhattisgarh', pincode: '',
  total_floors: 1, total_units: 0, year_built: '', cover_image: '', notes: '',
  is_active: true,
};

export default function BuildingsPage() {
  const { token } = useAuth();
  const api = authApi(token);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/erp/buildings').then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...blank, ...b, year_built: b.year_built || '' });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Name is required');
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.year_built === '') delete payload.year_built;
      else payload.year_built = Number(payload.year_built);
      payload.total_floors = Number(payload.total_floors || 1);
      payload.total_units = Number(payload.total_units || 0);
      if (editing) {
        await api.put(`/erp/buildings/${editing.id}`, payload);
        toast.success('Building updated');
      } else {
        await api.post('/erp/buildings', payload);
        toast.success('Building created');
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete building "${b.name}"?`)) return;
    try {
      await api.delete(`/erp/buildings/${b.id}`);
      toast.success('Deleted'); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="erp-buildings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Buildings</h1>
          <p className="text-gray-500 text-sm mt-1">Each building groups its floors, rooms and shops.</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-building-btn">
          <Plus className="h-4 w-4 mr-2" /> New Building
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No buildings yet. Add your first!</CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((b) => (
            <Card key={b.id} data-testid={`building-card-${b.name.replace(/\s+/g, '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg"><Building2 className="h-6 w-6 text-brand-orange" /></div>
                    <div>
                      <h3 className="font-heading font-semibold text-gray-900">{b.name}</h3>
                      <p className="text-xs text-gray-500">{[b.address, b.city].filter(Boolean).join(', ')}</p>
                    </div>
                  </div>
                  {!b.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="bg-gray-50 rounded p-2"><p className="font-heading text-lg font-bold text-gray-900">{b.total_floors}</p><p className="text-gray-500">Floors</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="font-heading text-lg font-bold text-gray-900">{b.total_units}</p><p className="text-gray-500">Units</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="font-heading text-lg font-bold text-gray-900">{b.year_built || '—'}</p><p className="text-gray-500">Built</p></div>
                </div>
                <div className="mt-4 flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => openEdit(b)}><Edit className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(b)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="building-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Building' : 'New Building'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1 md:col-span-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="building-name" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Address</Label>
                <Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>City</Label>
                <Input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>State</Label>
                <Input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Pincode</Label>
                <Input value={form.pincode || ''} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Year Built</Label>
                <Input type="number" value={form.year_built || ''} onChange={(e) => setForm({ ...form, year_built: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Total Floors</Label>
                <Input type="number" value={form.total_floors || 0} onChange={(e) => setForm({ ...form, total_floors: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Total Units (for reference)</Label>
                <Input type="number" value={form.total_units || 0} onChange={(e) => setForm({ ...form, total_units: e.target.value })} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="!m-0">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
